import type { OptimisticMessage, MessageReaction } from "@/lib/types/channel";

export type MessagesState = {
  messages: OptimisticMessage[];
  threadParent: OptimisticMessage | null;
};

export type MessagesAction =
  | { type: "INIT"; messages: OptimisticMessage[] }
  | { type: "APPEND_OPTIMISTIC"; message: OptimisticMessage }
  | { type: "APPEND_BATCH"; messages: OptimisticMessage[] }
  | { type: "CONFIRM_OPTIMISTIC"; tempId: string; confirmed: OptimisticMessage }
  | { type: "FAIL_OPTIMISTIC"; tempId: string }
  | { type: "PATCH"; messageId: string; patch: Partial<OptimisticMessage> }
  | { type: "SOFT_DELETE"; messageId: string }
  | {
      type: "TOGGLE_REACTION";
      messageId: string;
      reaction: MessageReaction;
      action: "added" | "removed";
    }
  | { type: "SET_THREAD_PARENT"; message: OptimisticMessage | null }
  | {
      type: "PREPEND_OLDER";
      messages: OptimisticMessage[];
      nextCursor?: string;
    }
  | { type: "SET_OLDER_CURSOR"; cursor: string | undefined };

export type MessagesStateWithCursor = MessagesState & {
  olderCursor: string | undefined;
  hasOlderMessages: boolean;
};

const initialState: MessagesStateWithCursor = {
  messages: [],
  threadParent: null,
  olderCursor: undefined,
  hasOlderMessages: false,
};

export function messagesReducer(
  state: MessagesStateWithCursor,
  action: MessagesAction,
): MessagesStateWithCursor {
  switch (action.type) {
    case "INIT":
      return { ...state, messages: action.messages };

    case "APPEND_OPTIMISTIC":
      return { ...state, messages: [...state.messages, action.message] };

    case "APPEND_BATCH":
      return { ...state, messages: [...state.messages, ...action.messages] };

    case "CONFIRM_OPTIMISTIC":
      return {
        ...state,
        messages: state.messages.map((m) =>
          m.id === action.tempId ? action.confirmed : m,
        ),
      };

    case "FAIL_OPTIMISTIC":
      return {
        ...state,
        messages: state.messages.map((m) =>
          m.id === action.tempId ? { ...m, optimisticStatus: "failed" } : m,
        ),
      };

    case "PATCH":
      return {
        ...state,
        messages: state.messages.map((m) =>
          m.id === action.messageId ? { ...m, ...action.patch } : m,
        ),
        // Keep the thread-parent snapshot in sync (e.g. its reply count) so the
        // thread-panel header doesn't drift from the reply list it renders.
        threadParent:
          state.threadParent?.id === action.messageId
            ? { ...state.threadParent, ...action.patch }
            : state.threadParent,
      };

    case "SOFT_DELETE":
      return {
        ...state,
        messages: state.messages.map((m) =>
          m.id === action.messageId
            ? { ...m, deletedAt: new Date().toISOString(), content: "" }
            : m,
        ),
      };

    case "TOGGLE_REACTION": {
      return {
        ...state,
        messages: state.messages.map((m) => {
          if (m.id !== action.messageId) return m;
          const filtered = m.reactions.filter(
            (r) =>
              !(
                r.emoji === action.reaction.emoji &&
                r.memberId === action.reaction.memberId
              ),
          );
          const reactions =
            action.action === "added"
              ? [...filtered, action.reaction]
              : filtered;
          return { ...m, reactions };
        }),
      };
    }

    case "SET_THREAD_PARENT":
      return { ...state, threadParent: action.message };

    case "PREPEND_OLDER":
      return {
        ...state,
        messages: [...action.messages, ...state.messages],
        olderCursor: action.nextCursor,
        hasOlderMessages: !!action.nextCursor,
      };

    case "SET_OLDER_CURSOR":
      return {
        ...state,
        olderCursor: action.cursor,
        hasOlderMessages: !!action.cursor,
      };

    default:
      return state;
  }
}

export { initialState };
